/****************
 * The web app's view of the shared order vocabulary.
 *
 * Everything that decides WHAT happened and what to say about it lives in
 * orderOutcome.core.ts, which is byte-identical to the phone app's copy. This
 * file adds the only thing that cannot be shared: Tailwind class names.
 *
 * Import from here, not from the core file, so the day a second app-specific
 * concern appears there is one place to put it.
 ****************/

import i18n from '@/i18n';
import * as core from './orderOutcome.core';
import type { Outcome, ReplicatedOrderLike } from './orderOutcome.core';

export * from './orderOutcome.core';

/****************
 * THE WORDS, IN THE READER'S LANGUAGE.
 *
 * The core decides WHICH sentence applies and stays byte-identical to the
 * phone's copy, so it cannot import i18n. These four shadow the core's
 * exports of the same name: the core's answer picks the key (and says when
 * there is nothing to say), locales/en/messages.json carries the text. A test
 * (orderOutcome.i18n.test.ts) holds the English there equal to the core's.
 ****************/
function brokerName(broker: string): string {
  return broker === core.DEFAULT_BROKER ? i18n.t('messages:outcome.defaultBroker') : broker;
}

export function labelFor(outcome: Outcome, broker: string = core.DEFAULT_BROKER): string {
  return i18n.t(`messages:outcome.label.${outcome}`, {
    broker: brokerName(broker),
    defaultValue: core.labelFor(outcome, broker),
  });
}

export function labelForOrder(
  order: ReplicatedOrderLike,
  outcome: Outcome,
  broker: string = core.DEFAULT_BROKER,
): string {
  if (outcome === 'working' &&
      (order.inferred_order_type || '').toUpperCase() === 'LIMIT') {
    return i18n.t('messages:outcome.label.limitWaiting');
  }
  return labelFor(outcome, broker);
}

export function explanationFor(
  outcome: Outcome,
  broker: string = core.DEFAULT_BROKER,
): string | null {
  const en = core.explanationFor(outcome, broker);
  if (en == null) return en;
  return i18n.t(`messages:outcome.explain.${outcome}`, { broker: brokerName(broker), defaultValue: en });
}

export function nextStepFor(outcome: Outcome): string | null {
  const en = core.nextStepFor(outcome);
  if (en == null) return en;
  return i18n.t(`messages:outcome.next.${outcome}`, { defaultValue: en });
}

/** Tailwind text colours, matching the mobile tones. */
export const OUTCOME_TONE: Record<Outcome, string> = {
  // Neither good nor bad news -- a session starting and a session being killed
  // are the same KIND of event, and the row's own words carry the weight.
  lifecycle: 'text-primary',
  filled: 'text-success',
  // NOT success green. The order is at the broker and has not filled, and
  // green is what made four unfilled DECREASE orders read as done. Primary,
  // like a signal sent: something is in motion and nothing has concluded.
  working: 'text-primary',
  signalled: 'text-primary',
  pending: 'text-muted-foreground',
  notListed: 'text-muted-foreground',
  insufficientFunds: 'text-warning',
  noPosition: 'text-muted-foreground',
  strategy: 'text-muted-foreground',
  // Muted, NOT a warning. The user paused it themselves; colouring the
  // consequence amber would report their own decision back to them as a problem.
  paused: 'text-muted-foreground',
  killSwitch: 'text-warning',
  needsSetting: 'text-warning',
  ourFault: 'text-destructive',
  // Muted. Nothing went wrong and nothing is waiting -- colouring a duplicate
  // record red or amber is how it came to be read as a failure in the first place.
  settled: 'text-muted-foreground',
  // The exchange refused it, so the trade did not happen. Amber rather than
  // red: these are conditions, not breakages -- the account, the size, or the
  // contract -- and every one of them can be true again tomorrow.
  venueNoMargin: 'text-warning',
  venueOrderTooSmall: 'text-warning',
  // The one refusal on this list the user can act on directly: their own
  // leverage and stop distance put the stop past their liquidation price.
  venueStopBeyondLiquidation: 'text-warning',
  // The price had passed the stop or target before the order arrived. Amber
  // like the one above, for the same reason: the position is open without it.
  venueTriggerAlreadyReached: 'text-warning',
  // Muted. The exchange capped open interest and is taking only closing
  // orders; nothing is wrong with the account and it clears by itself.
  venuePaused: 'text-muted-foreground',
  /****************
   * A TRADE THAT DID NOT HAPPEN AND NOBODY CAN SAY WHY.
   *
   * Warning, and NOT by copying the refusals above it -- that reflex is worth
   * resisting, because this is not a refusal. The venue ACCEPTED the order.
   *
   * Every muted entry on this list shares one thing: nothing went wrong.
   * A strategy rule excluded a trade, the user paused their own session, a
   * duplicate record was kept for the audit trail, the exchange capped open
   * interest and it clears by itself. Skipping those rows costs the reader
   * nothing.
   *
   * This one is different. The user expected to be in a trade, they are not,
   * and unlike venuePaused there is no named cause and nothing that clears on
   * its own -- the venue took the order and did nothing with it. It is the one
   * outcome here where the system genuinely does not know why. That deserves a
   * reader's attention.
   *
   * Not destructive either: nothing broke, nothing was spent, no position of
   * theirs is affected. destructive is for ourFault and failed, where something
   * is actually wrong.
   ****************/
  /*
   * Muted. Nothing went wrong and there is nothing to do: the trader left,
   * so the order stopped being worth filling and we cancelled it. Colouring
   * a rule working correctly as a warning is how `paused` would have been
   * wrong too -- it reports the user's own copying back to them as a fault.
   */
  traderLeftBeforeItFilled: 'text-muted-foreground',
  cancelledByYou: 'text-muted-foreground',
  tooSmallNotSent: 'text-warning',
  nothingToClose: 'text-muted-foreground',
  venueTradingOff: 'text-warning',
  acceptedButNeverFilled: 'text-warning',
  failed: 'text-destructive',
};
